import { createContext, useContext, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { workspacesApi } from '@/shared/api/endpoints';
import type { Workspace } from '@/shared/api/types';
import { getSelectedWorkspaceId, setSelectedWorkspaceId } from './storage';

interface WorkspaceContextValue {
  workspaces: Workspace[];
  active: Workspace | null;
  loading: boolean;
  select: (id: string) => void;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const query = useQuery({ queryKey: ['workspaces'], queryFn: workspacesApi.list, staleTime: 30_000 });
  const selectedId = getSelectedWorkspaceId();
  const active =
    query.data?.find((workspace) => workspace.id === selectedId) ??
    query.data?.find((workspace) => workspace.is_default) ??
    query.data?.[0] ??
    null;

  useEffect(() => {
    if (active && active.id !== selectedId) setSelectedWorkspaceId(active.id);
  }, [active, selectedId]);

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      workspaces: query.data ?? [],
      active,
      loading: query.isLoading,
      select: (id) => {
        if (id === active?.id) return;
        setSelectedWorkspaceId(id);
        window.location.assign(window.location.pathname + window.location.search);
      },
    }),
    [active, query.data, query.isLoading],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceContextValue {
  const context = useContext(WorkspaceContext);
  if (!context) throw new Error('useWorkspace must be used inside WorkspaceProvider');
  return context;
}

